import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Clock,
  Bell,
  Mail,
  MessageSquare,
  UserCheck,
  AlertTriangle,
  ArrowRight,
  Play,
  Pause,
  Archive
} from 'lucide-react';
import { components, typography, palette, spacing } from '../utils/designTokens';

/**
 * PolicyCard Component
 *
 * Displays a policy summary card with:
 * - Header: Policy name + status badge + View policy button
 * - Scope: Environment/App targeting (environment specified at activation)
 * - Metric: Condition being monitored (e.g., "P95 Latency > 2000ms")
 * - Actions: Icons for configured actions (email, Slack, HITL, etc.)
 * - Metadata: Last triggered + trigger count in 24h
 */
const PolicyCard = ({ policy, onSelect }) => {
  const navigate = useNavigate();

  // Validate required fields
  if (!policy?.policy_id || !policy?.name) {
    console.error('PolicyCard: Missing required fields (policy_id or name)');
    return (
      <div
        className={`${components.card.bg} ${components.card.rounded} ${components.card.padding}`}
        style={{
          border: `1px solid ${palette.status.danger}`,
          color: palette.text.muted
        }}
      >
        Unable to load policy data
      </div>
    );
  }

  const {
    policy_id,
    name,
    description = null,
    status = 'draft',
    latest_version = null,
    scope = {},
    metric = {},
    actions = [],
    last_triggered_at = null,
    trigger_count_24h = 0,
    active_environments = []
  } = policy;

  // Status badge config
  const statusConfig = {
    active: {
      color: palette.status.success,
      bgColor: `${palette.status.success}20`,
      label: 'Active',
      icon: Play
    },
    draft: {
      color: palette.text.muted,
      bgColor: `${palette.text.muted}20`,
      label: 'Draft',
      icon: Pause
    },
    archived: {
      color: palette.text.muted,
      bgColor: `${palette.text.muted}15`,
      label: 'Archived',
      icon: Archive
    }
  };

  const statusInfo = statusConfig[status] || statusConfig.draft;
  const StatusIcon = statusInfo.icon;

  // Format metric condition as human-readable string
  const formatMetricCondition = (metric) => {
    if (!metric?.type) return '-';

    const typeLabels = {
      trace_latency_p95: 'P95 Latency',
      trace_latency_p99: 'P99 Latency',
      trace_latency_avg: 'Avg Latency',
      quality_score: 'Quality Score',
      quality_p20_pct: 'Poor Quality %',
      quality_p40_pct: 'Low Quality %',
      error_rate: 'Error Rate',
      security_violations: 'Security Violations',
      cost_per_trace: 'Cost per Trace'
    };

    const comparatorLabels = {
      '>': '>',
      '>=': '≥',
      '<': '<',
      '<=': '≤',
      '==': '=',
      '!=': '≠'
    };

    const typeLabel = typeLabels[metric.type] || metric.type;
    const comparator = comparatorLabels[metric.comparator] || metric.comparator || '>';
    const threshold = metric.threshold != null ? metric.threshold : '?';
    const unit = metric.type?.includes('latency') ? 'ms' : '';
    const window = metric.window ? ` (${metric.window})` : '';

    return `${typeLabel} ${comparator} ${threshold}${unit}${window}`;
  };

  // Format scope as human-readable string
  // Note: Workflow is not part of scope (detected after trace completion)
  const formatScope = (scope) => {
    const parts = [];
    if (scope.environment) parts.push(scope.environment);
    if (scope.app_name || scope.app_id) parts.push(scope.app_name || `App ${scope.app_id}`);
    return parts.length > 0 ? parts.join(' / ') : 'All';
  };

  // Action type icons
  const actionIcons = {
    respond_gracefully: { icon: Shield, label: 'Graceful Response' },
    raise_error: { icon: AlertTriangle, label: 'Raise Error' },
    notify_email: { icon: Mail, label: 'Email' },
    notify_slack: { icon: MessageSquare, label: 'Slack' },
    human_in_loop: { icon: UserCheck, label: 'Human Review' }
  };

  // Format last triggered time
  const formatLastTriggered = (timestamp) => {
    if (!timestamp) return 'Never';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '-';

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      return `${diffDays}d ago`;
    } catch (err) {
      console.error('PolicyCard: Failed to format timestamp', err);
      return '-';
    }
  };

  // Calculate status color for left border
  const getBorderColor = () => {
    if (status === 'active') {
      // Active policies: teal accent
      return palette.accents.primary.base;
    }
    if (status === 'draft') {
      // Draft: muted
      return palette.text.muted;
    }
    // Archived: very muted
    return components.card.borderColor;
  };

  const handleCardClick = () => {
    if (onSelect) {
      onSelect(policy);
    }
  };

  return (
    <div
      className={`${components.card.bg} ${components.card.rounded} ${components.card.padding}`}
      style={{
        borderLeft: `4px solid ${getBorderColor()}`,
        borderTop: `1px solid ${components.card.borderColor}`,
        borderRight: `1px solid ${components.card.borderColor}`,
        borderBottom: `1px solid ${components.card.borderColor}`,
        width: '100%',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease'
      }}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderTopColor = palette.accents.primary.base;
        e.currentTarget.style.borderRightColor = palette.accents.primary.base;
        e.currentTarget.style.borderBottomColor = palette.accents.primary.base;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderTopColor = components.card.borderColor;
        e.currentTarget.style.borderRightColor = components.card.borderColor;
        e.currentTarget.style.borderBottomColor = components.card.borderColor;
      }}
    >
      {/* Header: Policy name + status badge + View button */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3
            className={`${typography.size.sm} ${typography.weight.semibold}`}
            style={{ color: palette.text.primary }}
          >
            {name}
          </h3>
          <span
            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs"
            style={{
              backgroundColor: statusInfo.bgColor,
              color: statusInfo.color
            }}
          >
            <StatusIcon size={12} />
            {statusInfo.label}
          </span>
          {latest_version != null && (
            <span
              className="text-xs"
              style={{ color: palette.text.muted }}
            >
              v{latest_version}
            </span>
          )}
        </div>
        <button
          className="flex items-center gap-1 px-3 py-1.5 rounded-md transition-colors"
          style={{
            color: palette.text.supporting,
            fontSize: typography.size.sm
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = palette.neutrals.surfaceHover;
            e.currentTarget.style.color = palette.accents.primary.base;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = palette.text.supporting;
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
        >
          View policy
          <ArrowRight size={14} />
        </button>
      </div>

      {/* Description (if present) */}
      {description && (
        <div
          className={`${typography.size.sm} mb-3`}
          style={{ color: palette.text.supporting }}
        >
          {description}
        </div>
      )}

      {/* Scope info */}
      <div
        className={`${typography.size.sm} mb-2`}
        style={{ color: palette.text.muted }}
      >
        Scope: {formatScope(scope)}
      </div>

      {/* Active environments */}
      {active_environments.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <span className={typography.size.sm} style={{ color: palette.text.muted }}>
            Active in:
          </span>
          {active_environments.map((env) => (
            <span
              key={env}
              className="px-1.5 py-0.5 rounded text-xs"
              style={{
                backgroundColor: `${palette.status.success}20`,
                color: palette.status.success
              }}
            >
              {env}
            </span>
          ))}
        </div>
      )}

      {/* Divider */}
      <div
        className="mb-3"
        style={{
          borderTop: `1px solid ${components.card.borderColor}`,
          marginLeft: `-${spacing.md}`,
          marginRight: `-${spacing.md}`
        }}
      />

      {/* Metric condition */}
      <div className="flex items-center gap-2 mb-3">
        <Clock size={16} style={{ color: palette.text.muted }} />
        <span className={typography.size.sm} style={{ color: palette.text.supporting }}>
          Condition:
        </span>
        <span
          className={`${typography.size.sm} ${typography.weight.medium}`}
          style={{ color: palette.text.primary }}
        >
          {formatMetricCondition(metric)}
        </span>
      </div>

      {/* Actions configured */}
      <div className="flex items-center gap-2 mb-3">
        <Bell size={16} style={{ color: palette.text.muted }} />
        <span className={typography.size.sm} style={{ color: palette.text.supporting }}>
          Actions:
        </span>
        <div className="flex items-center gap-1.5 ml-1">
          {actions.length > 0 ? (
            actions.map((action, idx) => {
              const actionConfig = actionIcons[action.type] || { icon: Bell, label: action.type };
              const ActionIcon = actionConfig.icon;
              return (
                <span
                  key={idx}
                  title={actionConfig.label}
                  className="flex items-center justify-center w-6 h-6 rounded"
                  style={{
                    backgroundColor: `${palette.accents.primary.base}20`,
                    color: palette.accents.primary.base
                  }}
                >
                  <ActionIcon size={14} />
                </span>
              );
            })
          ) : (
            <span className={typography.size.sm} style={{ color: palette.text.muted }}>
              None configured
            </span>
          )}
        </div>
      </div>

      {/* Metadata: Last triggered + trigger count */}
      <div className="flex items-center justify-between">
        <div
          className={typography.size.sm}
          style={{ color: palette.text.supporting }}
        >
          Last triggered: {formatLastTriggered(last_triggered_at)}
        </div>
        {trigger_count_24h > 0 && (
          <div
            className={`${typography.size.sm} px-2 py-0.5 rounded`}
            style={{
              backgroundColor: `${palette.status.warning}20`,
              color: palette.status.warning
            }}
          >
            {trigger_count_24h} triggers (24h)
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyCard;
